import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";
import { media } from "@/lib/api";
import { Lightbox } from "./Lightbox";

const usePerView = () => {
  const get = () => (window.innerWidth >= 1024 ? 5 : window.innerWidth >= 640 ? 3 : 2);
  const [n, setN] = useState(get);
  useEffect(() => {
    const on = () => setN(get());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return n;
};

const Placeholders = ({ testid }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 px-4 sm:px-14" data-testid={`${testid}-placeholders`}>
    {[0, 1, 2, 3, 4].map((i) => (
      <div key={i} className={`aspect-[3/4] border-2 border-dashed border-stone-300 rounded flex flex-col items-center justify-center text-stone-400 text-sm gap-2 ${i > 1 ? "hidden sm:flex" : ""} ${i > 2 ? "sm:hidden lg:flex" : ""}`}>
        <ImageIcon className="w-6 h-6" />
        Image coming soon
      </div>
    ))}
  </div>
);

const Arrow = ({ dir, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    aria-label={dir === "l" ? "Previous" : "Next"}
    className={`absolute top-1/2 -translate-y-1/2 ${dir === "l" ? "left-1 sm:left-3" : "right-1 sm:right-3"} w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white shadow-md border border-stone-200 flex items-center justify-center text-charcoal hover:bg-crimson hover:text-white transition-colors z-10`}
  >
    {dir === "l" ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
  </button>
);

export const Carousel = ({ images = [], testid = "carousel" }) => {
  const perView = usePerView();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [zoom, setZoom] = useState(null);
  const max = Math.max(0, images.length - perView);

  useEffect(() => {
    if (idx > max) setIdx(0);
  }, [idx, max]);

  useEffect(() => {
    if (paused || max === 0) return undefined;
    const t = setInterval(() => setIdx((i) => (i >= max ? 0 : i + 1)), 3500);
    return () => clearInterval(t);
  }, [paused, max]);

  if (!images.length) return <Placeholders testid={testid} />;

  const go = (d) => setIdx((i) => (i + d < 0 ? max : i + d > max ? 0 : i + d));

  return (
    <div className="w-full" data-testid={testid} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="relative px-10 sm:px-16">
        {max > 0 && <Arrow dir="l" onClick={() => go(-1)} testid={`${testid}-prev`} />}
        <div className="overflow-hidden">
          <div className="flex items-center transition-transform duration-700 ease-out" style={{ transform: `translateX(-${(idx * 100) / perView}%)` }}>
            {images.map((src, i) => (
              <button
                key={`${src}-${i}`}
                className="shrink-0 px-2 group"
                style={{ width: `${100 / perView}%` }}
                onClick={() => setZoom(media(src))}
                data-testid={`${testid}-slide-${i}`}
              >
                <img src={media(src)} alt="" loading="lazy" className="block w-full h-auto shadow-sm group-hover:shadow-xl transition-shadow duration-300" />
              </button>
            ))}
          </div>
        </div>
        {max > 0 && <Arrow dir="r" onClick={() => go(1)} testid={`${testid}-next`} />}
      </div>
      {max > 0 && (
        <div className="flex justify-center gap-2 mt-8 flex-wrap px-4">
          {Array.from({ length: max + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`${testid}-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-7 bg-crimson" : "w-2 bg-stone-300 hover:bg-stone-400"}`}
            />
          ))}
        </div>
      )}
      <Lightbox src={zoom} onClose={() => setZoom(null)} testid={`${testid}-lightbox`} />
    </div>
  );
};
